import { ChangeEvent, FC } from "react";
import { UseFormReturn } from "react-hook-form";
import { MiAlert, MiDetailFields } from "@miplatform/components";
import { INPUT_TYPES } from "@miplatform/constants";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import {
  FOLLOW_UP_DISPLAY_OPTIONS,
  FOLLOW_UP_TRAY_FILTERS,
} from "../../ConfigCalendarSetList.constants";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";

export const CalendarTabView: FC<CalendarTabViewProps> = ({
  form,
  extraTabProps: { isReadOnly, id } = {},
}: CalendarTabViewProps) => {
  const { control, setValue, getValues, watch, trigger } = form;
  const calendarConfigs = watch("calendarConfigs");
  const trays = watch("trays");
  const index = calendarConfigs.findIndex((config) => config.id === id) || 0;
  const selectedTab = calendarConfigs[index];

  const detailsFields = [
    {
      label: "Calendar Tab Name",
      formControl: control,
      name: `calendarConfigs.${index}.name`,
      required: true,
      value: selectedTab?.name,
      disabled: isReadOnly,
      width: "50%",
    },
    {
      label: "Calendar Type",
      name: `calendarConfigs.${index}.calendarType`,
      value: selectedTab?.calendarType,
      required: true,
      disabled: true,
      width: "50%",
    },
  ];

  const recyclingRulesFields = [
    {
      label: "Hours or Days",
      name: `calendarConfigs.${index}.followUpDisplay`,
      formControl: control,
      required: true,
      value: selectedTab?.followUpDisplay,
      fieldType: INPUT_TYPES.SELECT,
      options: FOLLOW_UP_DISPLAY_OPTIONS,
      disabled: isReadOnly,
      width: "33.3%",
    },
    {
      label: "Default Follow-Up Delay",
      name: `calendarConfigs.${index}.followUpDefaultOffsetHours`,
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      value: selectedTab?.followUpDefaultOffsetHours,
      required: true,
      setValue: (floatValue: number | undefined) => {
        const value = floatValue?.toString() ?? "";
        const maxOffset = getValues(
          `calendarConfigs.${index}.followUpMaxOffsetHours`
        );
        setValue(`calendarConfigs.${index}.followUpDefaultOffsetHours`, value, {
          shouldValidate: true,
        });

        if (maxOffset && floatValue) {
          trigger(`calendarConfigs.${index}.followUpMaxOffsetHours`);
        }
      },
      allowNegative: false,
      width: "33.3%",
    },
    {
      label: "Maximum Follow-Up Delay",
      name: `calendarConfigs.${index}.followUpMaxOffsetHours`,
      formControl: control,
      fieldType: INPUT_TYPES.NUMERIC,
      value: selectedTab?.followUpMaxOffsetHours,
      required: true,
      setValue: (floatValue: number | undefined) => {
        const value = floatValue?.toString() ?? "";

        setValue(`calendarConfigs.${index}.followUpMaxOffsetHours`, value, {
          shouldValidate: true,
        });
      },
      allowNegative: false,
      width: "33.3%",
    },
  ];

  const attachedTrayField = [
    {
      label: "Attached Tray",
      name: `calendarConfigs.${index}.attachedTray`,
      formControl: control,
      required: true,
      fieldType: INPUT_TYPES.SELECT,
      options: trays
        .filter(({ filter }) => filter === FOLLOW_UP_TRAY_FILTERS)
        .map((tray) => ({
          label: tray.name,
          value: tray.id,
        })),
      handleChange: ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
        const attachedTray = trays.find((tray) => tray.id === value);
        setValue(`calendarConfigs.${index}.attachedTray`, attachedTray, {
          shouldDirty: true,
        });
      },
      disabled: isReadOnly,
      value: selectedTab?.attachedTray?.id,
    },
  ];

  return (
    <>
      <ConfigAccordionHeader title="Calendar Tab Details">
        <MiDetailFields detailFields={detailsFields} />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Recycling Rules">
        <MiDetailFields detailFields={recyclingRulesFields} />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Attached Tray">
        <MiDetailFields detailFields={attachedTrayField} />
        <MiAlert
          description="Configure the hold tab for your calendar. Orders will remain in this hold tab until follow up delay is reached, after which order will move into selected tray."
          sx={{ mt: 2, py: 0, px: 2 }}
        />
      </ConfigAccordionHeader>
    </>
  );
};

interface CalendarTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    id?: string;
  };
}
